import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useMyPolicies } from '@/hooks/usePolicies';
import {
  LayoutDashboard,
  Users,
  GitFork,
  FileText,
  CalendarDays,
  Clock,
  DollarSign,
  Building2,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  PlusCircle,
  ClipboardList,
  CalendarRange,
  CheckSquare,
  ShieldCheck,
  Monitor,
  Shield,
  Receipt,
  Landmark,
  Target,
  TrendingUp,
  AlertTriangle,
  Briefcase,
  KanbanSquare,
  UserSearch,
  Gavel,
  Eye,
  CreditCard,
  Trophy,
  Heart,
  Milestone,
  UserMinus,
  PlayCircle,
  Calculator,
  Wallet,
  ScrollText,
  BookOpen,
  BookUser,
  BarChart3,
  FileBarChart2,
  Gift,
  Settings,
  Bell,
  UserRoundCheck,
  Coins,
  Globe,
  GraduationCap,
  StickyNote,
  Hash,
  Pencil,
  ScanSearch,
  KeyRound,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar } from '@/components/ui/Avatar';
import { CompanyLogo } from '@/components/ui/CompanyLogo';
import { NotificationCenter } from '@/components/NotificationCenter';

interface NavItem {
  label: string;
  href: string;
  icon: ReactNode;
  disabled?: boolean;
  roles?: string[];
  permissions?: string[];
  // Hide the item when every effective role is in this list. Used to
  // suppress entries that a perm check would otherwise allow — e.g.
  // Employees stays HR-only even if the role somehow holds the perm.
  excludeRoles?: string[];
}

interface NavSection {
  label: string;
  href: string;
  icon: ReactNode;
  children?: NavItem[];
}

const primaryNav: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
  { label: 'My Profile', href: '/profile', icon: <Users className="h-4 w-4" /> },
  { label: 'Notes', href: '/notes', icon: <StickyNote className="h-4 w-4" /> },
  { label: 'Directory', href: '/directory', icon: <BookUser className="h-4 w-4" /> },
  // Employees module is HR / manager / finance / recruiter / legal only.
  // Plain employees and it_admin should use Directory instead — kept off
  // the list even if a stray perm grant would otherwise satisfy the gate.
  { label: 'Employees', href: '/employees', icon: <Users className="h-4 w-4" />, permissions: ['employee.list', 'employee.view', 'employee.view_all', 'employee.view_team'], excludeRoles: ['employee', 'it_admin'] },
  { label: 'Org Chart', href: '/org-chart', icon: <GitFork className="h-4 w-4" /> },
  { label: 'My Policies', href: '/policies/my', icon: <FileText className="h-4 w-4" /> },
  { label: 'Benefits', href: '/benefits', icon: <Shield className="h-4 w-4" /> },
  { label: 'IT Assets', href: '/assets', icon: <Monitor className="h-4 w-4" />, permissions: ['assets.view', 'assets.manage'] },
  { label: 'Asset Clearance', href: '/assets/clearance', icon: <Monitor className="h-4 w-4" />, permissions: ['assets.view', 'assets.manage'] },
  { label: 'Asset Reports', href: '/assets/reports', icon: <Monitor className="h-4 w-4" />, permissions: ['assets.view', 'assets.manage'] },
  { label: 'Visas', href: '/visa', icon: <Globe className="h-4 w-4" />, permissions: ['visa.manage'] },
  { label: 'Compensation', href: '/compensation', icon: <Coins className="h-4 w-4" />, permissions: ['compensation.view_all', 'compensation.view_team', 'compensation.propose', 'compensation.approve'] },
  { label: 'Compliance Training', href: '/compliance-training', icon: <GraduationCap className="h-4 w-4" />, permissions: ['compliance_training.manage'] },
  // Probation hidden for now — module not yet ready for user-facing rollout. Re-enable when finalized.
  // { label: 'Probation', href: '/probation', icon: <ClipboardList className="h-4 w-4" /> },
  { label: 'Help Desk', href: '/help', icon: <BookOpen className="h-4 w-4" /> },
  // Access Audit — visible to every authenticated user; no role/perm gate per spec.
  // The endpoint only exposes status + name + last_day for emails the caller submits.
  { label: 'Access Audit', href: '/access-audit', icon: <ScanSearch className="h-4 w-4" /> },
];

const leaveNav: NavSection = {
  label: 'Leave',
  href: '/leave',
  icon: <CalendarDays className="h-4 w-4" />,
  children: [
    { label: 'Dashboard', href: '/leave', icon: <LayoutDashboard className="h-3.5 w-3.5" /> },
    { label: 'Apply Leave', href: '/leave/apply', icon: <PlusCircle className="h-3.5 w-3.5" /> },
    { label: 'My Leaves', href: '/leave/my', icon: <ClipboardList className="h-3.5 w-3.5" /> },
    {
      label: 'Team Calendar',
      href: '/leave/team-calendar',
      icon: <CalendarRange className="h-3.5 w-3.5" />,
      permissions: ['leave.approve'],
    },
    {
      label: 'Approvals',
      href: '/leave/approvals',
      icon: <CheckSquare className="h-3.5 w-3.5" />,
      permissions: ['leave.approve'],
    },
    {
      label: 'Admin',
      href: '/leave/admin',
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
      permissions: ['leave.admin'],
    },
    {
      label: 'Pending (Org)',
      href: '/leave/admin/pending',
      icon: <CheckSquare className="h-3.5 w-3.5" />,
      permissions: ['leave.admin'],
    },
  ],
};

const attendanceNav: NavSection = {
  label: 'Attendance',
  href: '/attendance',
  icon: <Clock className="h-4 w-4" />,
  children: [
    { label: 'Clock In', href: '/attendance', icon: <Clock className="h-3.5 w-3.5" /> },
    { label: 'Timesheets', href: '/attendance/timesheets', icon: <ClipboardList className="h-3.5 w-3.5" /> },
    {
      label: 'Team',
      href: '/attendance/team',
      icon: <Users className="h-3.5 w-3.5" />,
      permissions: ['leave.approve'],
    },
  ],
};

const onboardingNav: NavSection = {
  label: 'Onboarding',
  href: '/onboarding',
  icon: <UserRoundCheck className="h-4 w-4" />,
  children: [
    { label: 'My Checklist', href: '/onboarding/me', icon: <ClipboardList className="h-3.5 w-3.5" /> },
    {
      label: 'Admin',
      href: '/onboarding/admin',
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
      permissions: ['employee.edit'],
    },
  ],
};

const payrollNav: NavSection = {
  label: 'Payroll',
  href: '/payroll',
  icon: <DollarSign className="h-4 w-4" />,
  children: [
    // ── Overview ──
    { label: 'Dashboard', href: '/payroll', icon: <LayoutDashboard className="h-3.5 w-3.5" />, permissions: ['payroll.view', 'payroll.run', 'payroll.approve', 'payroll.admin'] },

    // ── Monthly run flow (top-to-bottom = the actual cycle) ──
    { label: '1. Variable Pay', href: '/payroll/variable-pay', icon: <ScrollText className="h-3.5 w-3.5" />, permissions: ['payroll.run', 'payroll.approve', 'payroll.admin'] },
    { label: '2. Run Payroll', href: '/payroll/new', icon: <PlayCircle className="h-3.5 w-3.5" />, permissions: ['payroll.run', 'payroll.approve', 'payroll.admin'] },
    { label: '3. Bank Files', href: '/payroll/bank-files', icon: <Landmark className="h-3.5 w-3.5" />, permissions: ['payroll.admin'] },
    { label: '4. Statutory Reports', href: '/payroll/statutory-reports', icon: <FileBarChart2 className="h-3.5 w-3.5" />, permissions: ['payroll.view', 'payroll.run', 'payroll.approve', 'payroll.admin'] },

    // ── Setup / configuration ──
    { label: 'Components', href: '/payroll/components', icon: <Settings className="h-3.5 w-3.5" />, permissions: ['payroll.view', 'payroll.run', 'payroll.approve', 'payroll.admin'] },
    { label: 'Bulk Revisions', href: '/payroll/salary-revisions', icon: <TrendingUp className="h-3.5 w-3.5" />, permissions: ['payroll.run', 'payroll.approve', 'payroll.admin'] },

    // ── Per-employee additions ──
    { label: 'Loans', href: '/payroll/loans', icon: <Coins className="h-3.5 w-3.5" />, permissions: ['payroll.view', 'payroll.run', 'payroll.approve', 'payroll.admin'] },
    { label: 'Perquisites', href: '/payroll/perquisites', icon: <Wallet className="h-3.5 w-3.5" />, permissions: ['payroll.view', 'payroll.run', 'payroll.approve', 'payroll.admin'] },
    { label: 'Tax Declarations', href: '/payroll/tax-declarations', icon: <Calculator className="h-3.5 w-3.5" />, permissions: ['payroll.view', 'payroll.approve', 'payroll.admin'] },
    { label: 'FnF Certificates', href: '/payroll/fnf-certificates', icon: <UserMinus className="h-3.5 w-3.5" />, permissions: ['payroll.view', 'payroll.run', 'payroll.approve', 'payroll.admin'] },
    { label: 'Upload Historical Payslips', href: '/payroll/payslips/historical/upload', icon: <ScrollText className="h-3.5 w-3.5" />, permissions: ['payroll.admin'] },

    // ── Employee self-service (regular employees only see these) ──
    { label: 'My Payslips', href: '/payroll/payslips/me', icon: <Receipt className="h-3.5 w-3.5" /> },
    { label: 'Tax & Investments', href: '/payroll/tax-declaration/me', icon: <Calculator className="h-3.5 w-3.5" /> },
  ],
};

const performanceNav: NavSection = {
  label: 'Performance',
  href: '/performance',
  icon: <TrendingUp className="h-4 w-4" />,
  children: [
    { label: 'Overview', href: '/performance', icon: <LayoutDashboard className="h-3.5 w-3.5" /> },
    { label: 'Goals', href: '/performance/goals', icon: <Target className="h-3.5 w-3.5" /> },
    {
      label: 'PIP Tracker',
      href: '/performance/pip',
      icon: <AlertTriangle className="h-3.5 w-3.5" />,
      permissions: ['performance.pip'],
    },
    {
      // Visible to everyone — the backend scopes results to (own,
      // reviewing) for the caller, so the page is safe to expose
      // without a permission gate.
      label: 'My PIP',
      href: '/performance/pip/my',
      icon: <AlertTriangle className="h-3.5 w-3.5" />,
    },
  ],
};

const recruitmentNav: NavSection = {
  label: 'Recruitment',
  href: '/recruitment',
  icon: <Briefcase className="h-4 w-4" />,
  children: [
    { label: 'Dashboard', href: '/recruitment', icon: <LayoutDashboard className="h-3.5 w-3.5" /> },
    { label: 'Requisitions', href: '/recruitment/requisitions', icon: <UserSearch className="h-3.5 w-3.5" /> },
    { label: 'Pipeline', href: '/recruitment/pipeline', icon: <KanbanSquare className="h-3.5 w-3.5" /> },
    {
      label: 'Intake',
      href: '/recruitment/intake',
      icon: <FileBarChart2 className="h-3.5 w-3.5" />,
      permissions: ['recruitment.manage'],
    },
    { label: 'Pre-Onboarding', href: '/recruitment/pre-onboarding', icon: <FileText className="h-3.5 w-3.5" /> },
  ],
};

const disciplinaryNav: NavSection = {
  label: 'Disciplinary',
  href: '/disciplinary',
  icon: <Gavel className="h-4 w-4" />,
  children: [
    {
      label: 'Cases',
      href: '/disciplinary',
      icon: <ClipboardList className="h-3.5 w-3.5" />,
      permissions: ['disciplinary.view', 'disciplinary.manage'],
    },
    {
      label: 'Whistleblower Portal',
      href: '/whistleblower',
      icon: <Eye className="h-3.5 w-3.5" />,
    },
    {
      label: 'Whistleblower Queue',
      href: '/whistleblower/admin',
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
      permissions: ['whistleblower.investigate'],
    },
  ],
};

const separationNav: NavSection = {
  label: 'Separation & FnF',
  href: '/separation',
  icon: <UserMinus className="h-4 w-4" />,
  children: [
    {
      label: 'Dashboard',
      href: '/separation',
      icon: <LayoutDashboard className="h-3.5 w-3.5" />,
      permissions: ['separation.initiate', 'separation.approve'],
    },
    {
      label: 'In Notice Period',
      href: '/separation/notice-period',
      icon: <Clock className="h-3.5 w-3.5" />,
      permissions: ['separation.initiate', 'separation.approve'],
    },
    {
      label: 'Initiate Exit',
      href: '/separation/initiate',
      icon: <LogOut className="h-3.5 w-3.5" />,
      permissions: ['separation.initiate'],
    },
    {
      label: 'My Resignation',
      href: '/separation/resign',
      icon: <FileText className="h-3.5 w-3.5" />,
    },
    {
      label: 'Approvals',
      href: '/separation/approvals',
      icon: <CheckSquare className="h-3.5 w-3.5" />,
      permissions: ['separation.approve', 'leave.approve'],
    },
  ],
};

const expensesNav: NavSection = {
  label: 'Expenses',
  href: '/expenses',
  icon: <CreditCard className="h-4 w-4" />,
  children: [
    { label: 'My Claims', href: '/expenses', icon: <Receipt className="h-3.5 w-3.5" /> },
    {
      label: 'Pending My Approval',
      href: '/expenses/pending-my-approval',
      icon: <ClipboardList className="h-3.5 w-3.5" />,
    },
    {
      label: 'All Claims',
      href: '/expenses/admin',
      icon: <ClipboardList className="h-3.5 w-3.5" />,
      permissions: ['expense.list', 'expense.admin'],
    },
    {
      label: 'Categories (Admin)',
      href: '/expenses/admin/categories',
      icon: <ClipboardList className="h-3.5 w-3.5" />,
      permissions: ['expense.admin'],
    },
  ],
};

const engagementNav: NavSection = {
  label: 'Engagement',
  href: '/engagement/recognition',
  icon: <Heart className="h-4 w-4" />,
  children: [
    {
      label: 'Recognition Feed',
      href: '/engagement/recognition',
      icon: <Trophy className="h-3.5 w-3.5" />,
    },
    {
      label: 'My Surveys',
      href: '/my/surveys',
      icon: <ClipboardList className="h-3.5 w-3.5" />,
    },
    {
      label: 'Surveys (Admin)',
      href: '/engagement/surveys/admin',
      icon: <ClipboardList className="h-3.5 w-3.5" />,
      permissions: ['employee.edit'],
    },
    {
      label: 'Milestones',
      href: '/engagement/milestones',
      icon: <Milestone className="h-3.5 w-3.5" />,
      permissions: ['employee.edit'],
    },
  ],
};

// Letters is HR-only — there's no employee-facing entry. Employees receive
// generated letters as email PDFs + an in-app notification with a download
// link, so they don't need the module in their sidebar.
const lettersNav: NavSection = {
  label: 'Letters',
  href: '/letters/dashboard',
  icon: <FileText className="h-4 w-4" />,
  children: [
    {
      label: 'Dashboard',
      href: '/letters/dashboard',
      icon: <LayoutDashboard className="h-3.5 w-3.5" />,
      permissions: ['letter.manage'],
    },
    {
      label: 'All Letters',
      href: '/letters',
      icon: <ClipboardList className="h-3.5 w-3.5" />,
      permissions: ['letter.manage'],
    },
    {
      label: 'Templates',
      href: '/letters/templates',
      icon: <ClipboardList className="h-3.5 w-3.5" />,
      permissions: ['letter.manage'],
    },
    {
      label: 'Generate',
      href: '/letters/generate',
      icon: <PlusCircle className="h-3.5 w-3.5" />,
      permissions: ['letter.generate'],
    },
  ],
};

const loansNav: NavSection = {
  label: 'Loans',
  href: '/loans/my',
  icon: <Coins className="h-4 w-4" />,
  children: [
    {
      label: 'My Requests',
      href: '/loans/my',
      icon: <ClipboardList className="h-3.5 w-3.5" />,
      permissions: ['loan.request'],
    },
    {
      // Visible to anyone with `loan.request` so managers (who don't
      // have `loan.approve`) can still discover the requests they're
      // named on. The backend scopes the list per role: HR sees all,
      // managers see only the requests pointing at their email.
      label: 'Approve Requests',
      href: '/loans/requests',
      icon: <CheckSquare className="h-3.5 w-3.5" />,
      permissions: ['loan.request', 'loan.approve'],
    },
  ],
};

const learningNav: NavSection = {
  label: 'Skill Development',
  href: '/learning',
  icon: <BookOpen className="h-4 w-4" />,
  children: [
    {
      label: 'My Training',
      href: '/learning',
      icon: <LayoutDashboard className="h-3.5 w-3.5" />,
    },
    {
      label: 'Compliance Report',
      href: '/learning/compliance',
      icon: <ShieldCheck className="h-3.5 w-3.5" />,
      permissions: ['employee.edit', 'learning.admin'],
    },
  ],
};

const accommodationNav: NavSection = {
  label: 'Accommodations',
  href: '/accommodation',
  icon: <BookUser className="h-4 w-4" />,
  children: [
    {
      label: 'My Requests',
      href: '/accommodation',
      icon: <ClipboardList className="h-3.5 w-3.5" />,
    },
  ],
};

const managerNav: NavSection = {
  label: 'Manager',
  href: '/manager',
  icon: <Briefcase className="h-4 w-4" />,
  children: [
    // Sidebar visibility logic is permission-OR-role (see LeaveSidebarSection
    // below). Adding roles here so managers without the leave.approve perm
    // still see their menu, matching the backend's _require_manager_role
    // gate which accepts the role directly.
    {
      label: 'Dashboard',
      href: '/manager',
      icon: <LayoutDashboard className="h-3.5 w-3.5" />,
      permissions: ['leave.approve'],
      roles: ['manager', 'hr_admin', 'hr_country', 'superadmin'],
    },
    {
      label: 'Approval Hub',
      href: '/manager/approvals',
      icon: <CheckSquare className="h-3.5 w-3.5" />,
      permissions: ['leave.approve', 'expense.admin'],
      roles: ['manager', 'hr_admin', 'hr_country', 'superadmin'],
    },
    {
      label: 'Team Analytics',
      href: '/manager/analytics',
      icon: <TrendingUp className="h-3.5 w-3.5" />,
      permissions: ['leave.approve', 'reports.view'],
      roles: ['manager', 'hr_admin', 'hr_country', 'superadmin'],
    },
    // Delegations menu removed per HR (2026-05-28). Backend route /manager/delegations
    // remains live in case product re-introduces the feature.
  ],
};

const analyticsNav: NavSection = {
  label: 'Analytics',
  href: '/analytics',
  icon: <BarChart3 className="h-4 w-4" />,
  children: [
    {
      label: 'Dashboard',
      href: '/analytics',
      icon: <LayoutDashboard className="h-3.5 w-3.5" />,
      permissions: ['reports.view', 'reports.admin'],
    },
    {
      label: 'Reports',
      href: '/reports',
      icon: <FileBarChart2 className="h-3.5 w-3.5" />,
      permissions: ['reports.view', 'reports.admin'],
    },
    {
      label: 'Reports Dashboard',
      href: '/reports/dashboard',
      icon: <BarChart3 className="h-3.5 w-3.5" />,
      permissions: ['reports.view', 'reports.admin'],
    },
    {
      label: 'Saved Reports',
      href: '/reports/saved',
      icon: <FileBarChart2 className="h-3.5 w-3.5" />,
      permissions: ['reports.view', 'reports.admin'],
    },
    {
      label: 'Report Builder',
      href: '/analytics/reports',
      icon: <FileBarChart2 className="h-3.5 w-3.5" />,
      permissions: ['reports.admin'],
    },
  ],
};

const comingSoonNav: NavItem[] = [
  { label: 'Entities', href: '/entities', icon: <Building2 className="h-4 w-4" />, disabled: true },
];

interface AppLayoutProps {
  children: ReactNode;
}

function SidebarNavItem({ item }: { item: NavItem }) {
  if (item.disabled) {
    return (
      <div className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-zinc-400 cursor-not-allowed select-none">
        <span className="text-zinc-400">{item.icon}</span>
        <span>{item.label}</span>
        <span className="ml-auto text-xs text-zinc-400 font-medium">Soon</span>
      </div>
    );
  }

  return (
    <NavLink
      to={item.href}
      end={item.href === '/leave'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-zinc-900 text-white font-medium'
            : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
        )
      }
    >
      {item.icon}
      <span>{item.label}</span>
    </NavLink>
  );
}

function LeaveSidebarSection({
  section,
  effectiveRoles,
  hasAnyPermission,
}: {
  section: NavSection;
  effectiveRoles: string[];
  hasAnyPermission: (perms: string[]) => boolean;
}) {
  const location = useLocation();
  const isLeaveActive =
    location.pathname === section.href || location.pathname.startsWith(`${section.href}/`);
  const [open, setOpen] = useState(isLeaveActive);

  const visibleChildren = (section.children ?? []).filter(
    (child) =>
      (!child.roles && !child.permissions) ||
      (child.roles && child.roles.some((r) => effectiveRoles.includes(r))) ||
      (child.permissions && hasAnyPermission(child.permissions))
  );

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
          isLeaveActive
            ? 'text-zinc-900 font-medium'
            : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
        )}
      >
        {section.icon}
        <span className="flex-1 text-left">{section.label}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-zinc-400 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="ml-3 mt-0.5 space-y-0.5 border-l border-zinc-200 pl-3">
          {visibleChildren.map((child) => (
            <NavLink
              key={child.href}
              to={child.href}
              end={child.href === section.href}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'bg-zinc-900 text-white font-medium'
                    : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700'
                )
              }
            >
              {child.icon}
              <span>{child.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function PendingPoliciesBanner() {
  const { data } = useMyPolicies();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const pending = data?.total_pending ?? 0;
  const overdue = data?.total_overdue ?? 0;
  const total = pending + overdue;

  // Don't show on the policies pages themselves
  const onPolicyPage = location.pathname.startsWith('/policies');
  if (!total || dismissed || onPolicyPage) return null;

  const isOverdue = overdue > 0;
  return (
    <div className={cn(
      'shrink-0 flex items-center gap-3 px-4 py-2.5 text-sm',
      isOverdue
        ? 'bg-red-50 border-b border-red-200'
        : 'bg-amber-50 border-b border-amber-200'
    )}>
      <AlertTriangle className={cn('h-4 w-4 shrink-0', isOverdue ? 'text-red-600' : 'text-amber-600')} />
      <span className={cn('flex-1', isOverdue ? 'text-red-800' : 'text-amber-800')}>
        You have{' '}
        <strong>
          {total} {total === 1 ? 'policy' : 'policies'}
        </strong>{' '}
        {isOverdue ? 'overdue' : 'pending'} acknowledgement.
      </span>
      <Link
        to="/policies/my"
        className={cn(
          'rounded-md px-3 py-1 text-xs font-medium transition-colors shrink-0',
          isOverdue
            ? 'bg-red-600 text-white hover:bg-red-500'
            : 'bg-amber-600 text-white hover:bg-amber-500'
        )}
      >
        Review now
      </Link>
      <button
        onClick={() => setDismissed(true)}
        className={cn('shrink-0', isOverdue ? 'text-red-400 hover:text-red-700' : 'text-amber-400 hover:text-amber-700')}
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, logout, switchRole, hasAnyPermission } = useAuth();
  const navigate = useNavigate();

  const userRoles = user?.roles ?? [];
  // Drive sidebar nav from the session's effective_roles (what the JWT carries),
  // which already reflects an in-progress role switch.
  const effectiveRoles = user?.effective_roles?.length ? user.effective_roles : userRoles;
  // Admin section is gated on the *current* session being superadmin, not the
  // permanent DB identity — a superadmin who switched to a lower role should
  // not see superadmin-only nav.
  const isSuperadmin = user?.currently_superadmin ?? false;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roleSwitcherOpen, setRoleSwitcherOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const roleSwitcherRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!roleSwitcherOpen) return;
    function handleOutsideClick(e: MouseEvent) {
      if (roleSwitcherRef.current && !roleSwitcherRef.current.contains(e.target as Node)) {
        setRoleSwitcherOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [roleSwitcherOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSwitchRole = async (targetRole: string | null) => {
    if (switching) return;
    setSwitching(true);
    setRoleSwitcherOpen(false);
    try {
      await switchRole(targetRole);
      // Hard redirect so the full app re-initialises with the new role token.
      // This guarantees all queries, component state, and cached data are completely fresh.
      window.location.href = '/dashboard';
    } catch {
      setSwitching(false);
    }
  };

  // All roles the user can switch between — superadmin first if applicable, then DB roles
  const availableRoles: string[] = [
    ...(user?.is_superadmin ? ['superadmin'] : []),
    ...userRoles.filter((r) => r !== 'superadmin'),
  ];
  const canSwitchRoles = availableRoles.length > 1;

  const roleLabel = (role: string | null): string => {
    if (!role) return 'Full Access';
    const labels: Record<string, string> = {
      employee: 'Employee',
      hr_admin: 'HR Admin',
      hr_country: 'HR Country',
      manager: 'Manager',
      superadmin: 'Super Admin',
      finance: 'Finance',
      it_admin: 'IT Admin',
      recruiter: 'Recruiter',
      legal: 'Legal',
    };
    return labels[role] ?? role.split('_').map((w) => (w[0]?.toUpperCase() ?? '') + w.slice(1)).join(' ');
  };

  const sidebar = (
    <aside
      className={cn(
        'flex h-full flex-col bg-zinc-50 border-r border-zinc-200',
        'w-64 shrink-0'
      )}
    >
      {/* Logo — fixed 0.5 in / 48 px square per org branding (see lib/branding.ts).
          Header bar grew from h-14 to h-16 so the larger logo has breathing room. */}
      <div className="flex h-16 items-center gap-3 border-b border-zinc-200 px-4">
        <CompanyLogo />
        <span className="text-sm font-semibold text-zinc-900 tracking-tight">
          Ultrahuman HRMS
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {primaryNav
          .filter((item) => {
            if (
              item.excludeRoles &&
              effectiveRoles.length > 0 &&
              effectiveRoles.every((r) => item.excludeRoles!.includes(r))
            ) {
              return false;
            }
            return (
              (!item.roles && !item.permissions) ||
              (item.roles && item.roles.some((r) => effectiveRoles.includes(r))) ||
              (item.permissions && hasAnyPermission(item.permissions))
            );
          })
          .map((item) => (
            <SidebarNavItem key={item.href} item={item} />
          ))}

        {/* Request Asset — hidden for HR/IT admin who manage assets, not request them */}
        {!hasAnyPermission(['assets.manage']) && !isSuperadmin && (
          <SidebarNavItem
            item={{ label: 'Request Asset', href: '/assets/request', icon: <Monitor className="h-4 w-4" /> }}
          />
        )}

        {/* SaaS Access — every authenticated user can request. The
            reporting manager approves + grants in one step (reachable
            via /manager/saas-approvals, deep-linked from notifications).
            HR is notification-only, no queue. */}
        <SidebarNavItem
          item={{ label: 'Request SaaS Access', href: '/saas/request', icon: <KeyRound className="h-4 w-4" /> }}
        />

        <div className="my-3 border-t border-zinc-200" />
        <LeaveSidebarSection section={leaveNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />

        <div className="my-3 border-t border-zinc-200" />
        <LeaveSidebarSection section={attendanceNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />

        {/*
         * Onboarding + Recruitment hidden for now across all roles — modules
         * are not user-ready (#247 found over-restricted gates, #248 perm
         * gaps, #229 token-mint vulnerability). Sidebar entries return when
         * the modules are signed off for rollout. Underlying routes still
         * resolve via direct URL for admin testing.
         *
         * Keeping the nav definitions alive (void-ref below) so the next
         * person to re-enable just deletes the void line + uncomments the
         * <LeaveSidebarSection> usages instead of rewriting the structure.
         */}
        {void onboardingNav}
        {void recruitmentNav}

        <div className="my-3 border-t border-zinc-200" />
        <LeaveSidebarSection section={payrollNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />

        <div className="my-3 border-t border-zinc-200" />
        <LeaveSidebarSection section={performanceNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />

        {/* <LeaveSidebarSection section={recruitmentNav} userRoles={effectiveRoles} /> */}

        <div className="my-3 border-t border-zinc-200" />
        <LeaveSidebarSection section={disciplinaryNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />

        <div className="my-3 border-t border-zinc-200" />
        <LeaveSidebarSection section={separationNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />

        <div className="my-3 border-t border-zinc-200" />
        <LeaveSidebarSection section={expensesNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />

        <div className="my-3 border-t border-zinc-200" />
        <LeaveSidebarSection section={engagementNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />

        {/* Letters is HR-only; the section is hidden entirely for users
            without `letter.manage` or `letter.generate`. */}
        {hasAnyPermission(['letter.manage', 'letter.generate']) && (
          <>
            <div className="my-3 border-t border-zinc-200" />
            <LeaveSidebarSection section={lettersNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />
          </>
        )}

        {/* Loans — every authenticated user has `loan.request` so the
            section always renders. The Approve Requests child is gated
            on `loan.approve` (HR / Super Admin only) at child level. */}
        {hasAnyPermission(['loan.request', 'loan.approve']) && (
          <>
            <div className="my-3 border-t border-zinc-200" />
            <LeaveSidebarSection section={loansNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />
          </>
        )}

        <div className="my-3 border-t border-zinc-200" />
        <LeaveSidebarSection section={learningNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />

        <div className="my-3 border-t border-zinc-200" />
        <LeaveSidebarSection section={accommodationNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />

        {/* Analytics section */}
        {hasAnyPermission(['reports.view', 'reports.admin']) && (
          <>
            <div className="my-3 border-t border-zinc-200" />
            <LeaveSidebarSection section={analyticsNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />
          </>
        )}

        {/* Manager section — visible only to managers and HR */}
        {hasAnyPermission(['leave.approve']) && (
          <>
            <div className="my-3 border-t border-zinc-200" />
            <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-zinc-400">
              Manager
            </p>
            <LeaveSidebarSection section={managerNav} effectiveRoles={effectiveRoles} hasAnyPermission={hasAnyPermission} />
          </>
        )}

        {/* Documents inbox — HR-admin / hr_country review queue. Surfaced
            outside the superadmin block so hr_admin sees it. Gated on the
            same `employee.edit` perm the API uses. */}
        {(hasAnyPermission(['employee.edit']) || isSuperadmin) && (
          <>
            <div className="my-3 border-t border-zinc-200" />
            <SidebarNavItem
              item={{
                label: 'Documents',
                href: '/admin/documents',
                icon: <FileText className="h-4 w-4" />,
              }}
            />
          </>
        )}

        {/* HR Admin section — visible to hr_admin / hr_country / superadmin.
            These are HR-owned admin surfaces; the system-level Admin Panel
            (Manage Users, Deprovision Audit) is split out below. */}
        {(hasAnyPermission(['employee.edit', 'leave.admin', 'policy.manage', 'settings.manage']) || isSuperadmin) && (
          <>
            <div className="my-3 border-t border-zinc-200" />
            <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-zinc-400">
              HR Admin
            </p>
            <SidebarNavItem
              item={{
                label: 'All Policies',
                href: '/policies',
                icon: <FileText className="h-4 w-4" />,
              }}
            />
            <SidebarNavItem
              item={{
                label: 'HR Notifications',
                href: '/admin/hr-notifications',
                icon: <Bell className="h-4 w-4" />,
              }}
            />
            <SidebarNavItem
              item={{
                label: 'Holiday Calendars',
                href: '/admin/holidays',
                icon: <CalendarDays className="h-4 w-4" />,
              }}
            />
            {(hasAnyPermission(['settings.manage']) || isSuperadmin) && (
              <SidebarNavItem
                item={{
                  label: 'Org Structure',
                  href: '/admin/org-structure',
                  icon: <Building2 className="h-4 w-4" />,
                }}
              />
            )}
            {(hasAnyPermission(['settings.manage']) || isSuperadmin) && (
              <SidebarNavItem
                item={{
                  label: 'Legal Entities',
                  href: '/admin/legal-entities',
                  icon: <Building2 className="h-4 w-4" />,
                }}
              />
            )}
            {(hasAnyPermission(['settings.manage']) || isSuperadmin) && (
              <SidebarNavItem
                item={{
                  label: 'Compliance Reports',
                  href: '/admin/legal-entities/reports',
                  icon: <Building2 className="h-4 w-4" />,
                }}
              />
            )}
            {(hasAnyPermission(['settings.manage']) || isSuperadmin) && (
              <SidebarNavItem
                item={{
                  label: 'Slack Channels',
                  href: '/admin/slack-channels',
                  icon: <Hash className="h-4 w-4" />,
                }}
              />
            )}
            {/* Pre-Onboarding admin — also lives under the hidden Recruitment
                section. Surfacing it here so HR Admin / Super Admin can reach
                it without flipping the whole Recruitment sidebar on. */}
            {(hasAnyPermission(['recruitment.manage']) || isSuperadmin) && (
              <SidebarNavItem
                item={{
                  label: 'Pre-Onboarding',
                  href: '/recruitment/pre-onboarding',
                  icon: <FileText className="h-4 w-4" />,
                }}
              />
            )}
            <SidebarNavItem
              item={{
                label: 'Leave Management',
                href: '/leave/management',
                icon: <CalendarDays className="h-4 w-4" />,
              }}
            />
            <SidebarNavItem
              item={{
                label: 'Onboarding Feedback',
                href: '/engagement/onboarding-feedback/admin',
                icon: <FileBarChart2 className="h-4 w-4" />,
              }}
            />
            <SidebarNavItem
              item={{
                label: 'Feedback Forms (Edit)',
                href: '/engagement/onboarding-feedback/templates',
                icon: <Pencil className="h-4 w-4" />,
              }}
            />
            <SidebarNavItem
              item={{
                label: 'Benefits Catalog',
                href: '/admin/perks',
                icon: <Gift className="h-4 w-4" />,
              }}
            />
          </>
        )}

        {/* Admin Panel — superadmin only (Manage Users). Deprovisioning Audit
            is split out below because it_admin and legal also need access. */}
        {(isSuperadmin || hasAnyPermission(['system.audit.read'])) && (
          <>
            <div className="my-3 border-t border-zinc-200" />
            <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-zinc-400">
              Admin Panel
            </p>
            <SidebarNavItem
              item={{
                label: 'Manage Users',
                href: '/admin/users',
                icon: <Settings className="h-4 w-4" />,
              }}
            />
          </>
        )}

        {/* Deprovisioning Audit — permission-gated for DPRV-1/2/4. */}
        {(isSuperadmin || hasAnyPermission(['system.deprovision_audit.read'])) && (
          <>
            <div className="my-3 border-t border-zinc-200" />
            <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-zinc-400">
              Admin Panel
            </p>
            <SidebarNavItem
              item={{
                label: 'Deprovisioning Audit',
                href: '/admin/deprovision-audit',
                icon: <ShieldCheck className="h-4 w-4" />,
              }}
            />
          </>
        )}
        {isSuperadmin && (
          <>
            <div className="my-3 border-t border-zinc-200" />
            <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-zinc-400">
              Admin Panel
            </p>
            <SidebarNavItem
              item={{
                label: 'System Audit Log',
                href: '/admin/audit-log',
                icon: <Shield className="h-4 w-4" />,
              }}
            />
          </>
        )}

        <div className="my-3 border-t border-zinc-200" />
        <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-zinc-400">
          Coming Soon
        </p>
        {comingSoonNav.map((item) => (
          <SidebarNavItem key={item.href} item={item} />
        ))}
      </nav>

      {/* User + Role Switcher */}
      {user && (
        <div className="border-t border-zinc-200 p-3" ref={roleSwitcherRef}>
          {/* Role switcher popover — only rendered when user has multiple roles */}
          {roleSwitcherOpen && canSwitchRoles && (
            <div className="mb-2 rounded-lg border border-zinc-200 bg-white shadow-lg overflow-hidden">
              <p className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                Switch Dashboard
              </p>
              {availableRoles.map((role) => (
                <button
                  key={role}
                  onClick={() => handleSwitchRole(role)}
                  disabled={switching}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors',
                    user.active_role === role
                      ? 'bg-zinc-900 text-white font-medium'
                      : 'text-zinc-700 hover:bg-zinc-50'
                  )}
                >
                  <Shield className="h-3.5 w-3.5 shrink-0" />
                  {roleLabel(role)}
                  {user.active_role === role && (
                    <span className="ml-auto text-xs opacity-70">current</span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 rounded-md px-2 py-2">
            <button
              onClick={() => canSwitchRoles && setRoleSwitcherOpen((v) => !v)}
              className={cn(
                'flex min-w-0 flex-1 items-center gap-3 text-left',
                !canSwitchRoles && 'cursor-default'
              )}
              aria-label={canSwitchRoles ? 'Switch dashboard' : undefined}
            >
              <Avatar name={user.full_name} src={user.picture_url} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-zinc-900">{user.full_name}</p>
                <p className="truncate text-xs text-zinc-400">
                  {switching ? 'Switching…' : roleLabel(user.active_role)}
                </p>
              </div>
              {canSwitchRoles && (
                <ChevronDown className={cn(
                  'h-3.5 w-3.5 shrink-0 text-zinc-400 transition-transform',
                  roleSwitcherOpen && 'rotate-180'
                )} />
              )}
            </button>
            <button
              onClick={handleLogout}
              className="shrink-0 text-zinc-400 hover:text-zinc-700 transition-colors"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">{sidebar}</div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-zinc-900/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative z-50 flex h-full w-64 flex-col">
            {sidebar}
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex h-14 shrink-0 items-center gap-4 border-b border-zinc-200 bg-white px-4">
          {/* Mobile hamburger */}
          <button
            className="lg:hidden text-zinc-500 hover:text-zinc-700 transition-colors"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          {/* Breadcrumb — rendered by pages via context if needed; placeholder */}
          <nav className="flex items-center gap-1 text-sm text-zinc-500 flex-1">
            <span className="text-zinc-500">People</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <NotificationCenter />

            {user && (
              <Avatar
                name={user.full_name}
                src={user.picture_url}
                size="sm"
                className="cursor-pointer"
              />
            )}
          </div>
        </header>

        {/* Pending policy acknowledgement banner */}
        <PendingPoliciesBanner />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto relative bg-zinc-50">
          {children}
        </main>
      </div>
    </div>
  );
}
